import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Pencil1Icon } from '@radix-ui/react-icons';

import { KeyboardKeys } from '../../../assets/enums/keyboard-keys.enum';

const MAX_NAME_LENGTH = 64;

type AssetNameEditorProps = {
  name: string;
  onRename: (name: string) => void;
};

export const AssetNameEditor: React.FC<AssetNameEditorProps> = ({ name, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);
  const isCancelled = useRef(false);

  const startEditing = () => {
    setValue(name);
    isCancelled.current = false;
    setIsEditing(true);
  };

  useLayoutEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const commit = () => {
    setIsEditing(false);

    if (isCancelled.current) {
      return;
    }

    const trimmed = value.trim();
    if (trimmed && trimmed !== name) {
      onRename(trimmed);
    }
  };

  const cancel = () => {
    isCancelled.current = true;
    setValue(name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KeyboardKeys.Enter) {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === KeyboardKeys.Escape) {
      e.preventDefault();
      cancel();
    }
  };

  if (isEditing) {
    return (
      // The wrapper's ::after mirrors the value so the input sizes to its text.
      <InputSizer data-value={value}>
        <NameInput
          ref={inputRef}
          value={value}
          maxLength={MAX_NAME_LENGTH}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          // Editing happens "in place" inside the breadcrumb crumb, so stop the
          // crumb's own click handler from firing while typing.
          onClick={(e) => e.stopPropagation()}
        />
      </InputSizer>
    );
  }

  return (
    <Container>
      <AssetName>{name}</AssetName>
      <EditButton type="button" aria-label="Rename asset" title="Rename" onClick={startEditing}>
        <Pencil1Icon />
      </EditButton>
    </Container>
  );
};

const Container = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
`;

const AssetName = styled.span`
  color: inherit;
  font: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EditButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  color: inherit;
  opacity: 0;
  transition: opacity 0.1s ease-in;

  svg {
    width: 1.3rem;
    height: 1.3rem;
  }

  ${Container}:hover &,
  ${Container}:focus-within & {
    opacity: 0.7;
  }

  &:hover {
    opacity: 1 !important;
  }
`;

const InputSizer = styled.span`
  display: inline-grid;
  align-items: center;
  max-width: 100%;
  vertical-align: bottom;

  /* The input and the mirror share the same grid cell, so the cell — and thus
   * the input — is exactly as wide as the current text. */
  &::after,
  & > input {
    grid-area: 1 / 1;
    width: auto;
    min-width: 1ch;
    font: inherit;
    margin: 0;
    padding: 0;
    white-space: pre;
  }

  &::after {
    content: attr(data-value) ' ';
    visibility: hidden;
  }
`;

const NameInput = styled.input`
  color: inherit;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--color-secondary);
  outline: none;
`;
